import React, {useEffect, useRef, useState} from 'react';
import {Layers2, ListFilter, Plus, Search, Trash2, UserRound, Users} from 'lucide-react';
import {Link} from 'react-router-dom';
import catalog from './catalog.json';

function StrategyCard({record, busy, onRename, onDuplicate, onDelete}) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState('');
  const inputRef = useRef(null);
  const cancelled = useRef(false);
  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);
  const map = catalog.maps.find(item => item.id === record.mapId);
  const query = record.scope === 'team' ? '?' + new URLSearchParams({scope:'team', teamId:record.teamId}) : '';
  const href = '/strats-planner/' + record.id + query;
  const ScopeIcon = record.scope === 'team' ? Users : UserRound;

  function saveTitle() {
    if (cancelled.current) return;
    cancelled.current = true;
    setEditing(false);
    onRename(record, title);
  }

  return <article className="SP__strategyCard">
    <Link className="SP__openCard" to={href} aria-label={'Open ' + record.title}>
      <div className="SP__cardMap">
        <img src={map?.image} alt="" style={map?.name === 'Ascent' ? {transform:'rotate(90deg)'} : undefined}/>
        <span className={'SP__cardScope SP__cardScope--' + record.scope}><ScopeIcon size={13}/>{record.scope === 'team' ? 'Team' : 'Personal'}</span>
      </div>
    </Link>
    <div className="SP__cardDetails">
      {editing ? <input
        aria-label="Strategy name"
        maxLength={120}
        value={title}
        ref={inputRef}
        onChange={event => setTitle(event.target.value)}
        onBlur={saveTitle}
        onKeyDown={event => {
          if(event.key === 'Enter') {
            event.preventDefault();
            saveTitle();
          }
          if(event.key === 'Escape') {
            cancelled.current = true;
            setEditing(false);
          }
        }}
      /> : record.canEdit
        ? <button className="SP__cardTitle" title="Click to rename" disabled={busy} onClick={() => {cancelled.current=false;setTitle(record.title);setEditing(true);}}>{record.title}</button>
        : <h2>{record.title}</h2>}
      <p>{map?.name} · {record.side}</p>
      <small>Updated {new Intl.DateTimeFormat('en-GB',{day:'2-digit',month:'long',year:'numeric'}).format(new Date(record.updatedAt)).replace(/ /g,' / ')}</small>
      {map?.artworkAvailable === false && <small>Map artwork unavailable</small>}
      {record.canEdit && <div className="SP__cardActions"><button title="Duplicate strategy" aria-label={'Duplicate ' + record.title} disabled={busy} onClick={() => onDuplicate(record)}><Layers2 size={16}/></button><button type="button" className="SP__deleteStrategy" title="Delete strategy" aria-label={'Delete ' + record.title} disabled={busy} onClick={() => onDelete(record)}><Trash2 size={16}/></button></div>}
    </div>
  </article>;
}

export default function PlaybookLibrary({personal, team, teamId, canEditTeam, teamLoading, busy, onCreate, onRename, onDuplicate, onDelete}) {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [map, setMap] = useState('');
  const [createScope, setCreateScope] = useState('personal');
  const rows = [
    ...(filter !== 'team' ? (personal.data || []).map(record => ({...record, scope:'personal', teamId:null, canEdit:true})) : []),
    ...(filter !== 'personal' ? (team.data || []).map(record => ({...record, scope:'team', teamId, canEdit:canEditTeam})) : [])
  ].filter(record => (!map || record.mapId === map) && record.title.toLowerCase().includes(search.trim().toLowerCase()))
    .sort((a,b) => b.updatedAt - a.updatedAt);
  const destination = canEditTeam && createScope === 'team' ? {scope:'team', teamId} : {scope:'personal', teamId:null};
  const mapId = (catalog.maps.find(item => item.id === map && item.artworkAvailable !== false) || catalog.maps.find(item => item.name === 'Ascent')).id;

  return <>
    <header className="SP__heading"><div><h1>My Playbooks</h1><p>Your maps. Your plays. Your team.</p></div></header>
    <div className="SP__libraryFilters">
      <details className="SP__scopeFilter">
        <summary aria-label="Filter by strategy scope" title="Filter by strategy scope"><ListFilter size={17}/>{filter !== 'all' && <span>{filter === 'team' ? 'Team' : 'Personal'}</span>}</summary>
        <div aria-label="Strategy scope">
          {[['all','All strategies'],['personal','Personal'],['team','Team']].map(([value,label]) =>
            <button key={value} type="button" aria-pressed={filter === value} onClick={event => {
              setFilter(value);
              const menu = event.currentTarget.closest('details');
              menu.open = false;
              menu.querySelector('summary').focus();
            }}>{label}</button>
          )}
        </div>
      </details>
      <label><Search size={16}/><input aria-label="Search strategies" placeholder="Search strategies…" value={search} onChange={event => setSearch(event.target.value)}/></label>
      <select aria-label="Filter strategies by map" value={map} onChange={event => setMap(event.target.value)}>
        <option value="">All maps</option>{catalog.maps.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
      </select>
    </div>
    {(personal.error || team.error) && <p role="alert">{personal.error || team.error}</p>}
    {(personal.loading || team.loading || teamLoading) && <p role="status">Loading strategies…</p>}
    <div className="SP__libraryGrid">
      <article className="SP__strategyCard SP__createCard">
        <button type="button" className="SP__newStrategyCard" aria-label="Create new strategy" disabled={busy} onClick={() => onCreate(mapId,destination)}>
          <span className="SP__newStrategyIcon"><Plus size={34}/></span><strong>New strategy</strong><small>Start with a blank map</small>
        </button>
        {canEditTeam && <select aria-label="Save new strategy in" value={destination.scope} onChange={event => setCreateScope(event.target.value)}>
          <option value="personal">Personal playbook</option><option value="team">Team playbook</option>
        </select>}
      </article>
      {rows.map(record => <StrategyCard key={JSON.stringify([record.scope,record.teamId,record.id])} {...{record,busy,onRename,onDuplicate,onDelete}}/>)}
      {!rows.length && !personal.loading && !team.loading && <p className="SP__empty">No matching strategies.</p>}
    </div>
  </>;
}
